import RTPaaS from './RTPaaS.js';
import Constants from './Constants.js';
import Payload from './Payload.js';
import TesterKendoWindow from '../widgets/TesterKendoWindow';
import CaptureImage from './CaptureImage.js';
import SystemCheck from "../systemCheck/SystemCheck";
import CustomKendoWindow from "../widgets/CustomKendoWindow";
import AudioVideoClient from './AudioVideoClient.js';
import AudioCallAutoConnectMsg_Mp3 from '../../audio/groupCallAutoConnectMessage.mp3';
export default class RTPaaSTester extends RTPaaS {

	constructor(testerIdentifier, ablyOptions, config) {
		super(config, ablyOptions);
		this.imgCaptureInterval = null;
		this.testerIdentifier = testerIdentifier;
		this.config = config;
		this.groupCallFirst = this.config?.groupCallFirst ?? false;
		this.isLocked = this.groupCallFirst;
		this.videoMonitoringMeetingId = null;
		this.activeVideoMonitoringInitialization = false;

		//Don't finish initialization until after ably is connected.
		const ablyConnection = this.establishAblyConnection();
		if (ablyConnection){
			ablyConnection.then(() => {
				this.clientId = this.ably.auth.clientId;
				this.init();
				this.createTesterWidget();
				this.isInitialized = true;
				this.unseenMessagesAry = [];
			}).catch((err) => {
				console.error(`RTPaaSTester: An error occurred during initialization: ${err}`);
			});
		}else {
			console.error(`RTPaaSTester: An error occurred during Ably connection.`);
		}

		// Start Tester by sending image to proctor, regardless of monitoring type.
		this.config.monitoringType = Constants.MonitoringType.BY_IMAGE;
		this.captureImage = new CaptureImage({
				"compressionValue": this.config?.compressionValue || 60,
				"imageFrequencyInSeconds": this.config?.imageFrequencyInSeconds || 3
			}
		);
	}

	init() {
		this.createChannelWithoutHistory(this.testerIdentifier);
		this.createChannelWithHistory(this.testerIdentifier);
		this.subscribeToChannels();

		// Initialize the captureImage object
		// BY_IMAGE, we will capture images at a set frequency.
		// BY_VIDEO, we will send image to the proctor as both a presence signal and
		// a placeholder to display in the image container until the video stream is available.
		this.captureImage.init().then((imgData) => {
			this.imgCaptureInterval = setInterval(() => {
				if (this.config.monitoringType === Constants.MonitoringType.BY_IMAGE) {
					const img = this.captureImage.captureImageFromStream();
					this.sendImageToProctor(img);
				}else {
					clearInterval(this.imgCaptureInterval);
				}
			}, this.captureImage.imageFrequencyInSeconds * 1000);
			this.sendImageToProctor(imgData);
		});

		document.addEventListener("visibilitychange", () => {
			if (document.visibilityState === "visible" && this.testerWindow && this.testerWindow.unseenChatCount <= 0) {
				this.sendUnseenReceivedChatMessage(this.testerIdentifier);
			}
		});
	}

	subscribeToChannels() {
		this.subscribeToChannelWithoutHistory(this.testerIdentifier, Constants.MessageNames.IMAGE, this.handleImageMessage.bind(this));
		this.subscribeToChannel(this.testerIdentifier, Constants.MessageNames.HAND, this.handleHandMessage.bind(this));
		this.subscribeToChannel(this.testerIdentifier, Constants.MessageNames.LOCK, this.handleLockMessage.bind(this));
		this.subscribeToChannel(this.testerIdentifier, Constants.MessageNames.CHAT, this.handleChatMessage.bind(this));
		this.subscribeToChannel(this.testerIdentifier, Constants.MessageNames.CALL, this.handleCallMessage.bind(this));
		this.subscribeToChannel(this.testerIdentifier, Constants.MessageNames.BROADCAST, this.handleBroadcastMessage.bind(this));
	}

	handleLockMessage(message) {
		if (message.data.eventType === Constants.EventTypes.UNLOCK_TESTER) {
			this.handleUnlockTester();
		}
	}

	createTesterWidget(){
		this.testerWindow = new TesterKendoWindow(this, this.testerIdentifier);
		let kendoWindow = this.testerWindow.kendoWindow;
		this.handButton = kendoWindow.wrapper.find('button[aria-label="Hand"]');
		this.attachListeners();
		this.renderHelpText();
		this.handleHistory();
	}

	renderHelpText() {
		let kendoChat = this.testerWindow.kendoChat;
		kendoChat.renderAttachments({
			attachments: [{
				contentType: "helpText",
				content: {
					title: "Help Text",
					text: rtp_i18next.t("testerWidget:testerHelpText"),
				}
			}],
			attachmentLayout: "list"
		}, kendoChat.getUser());
	}

	attachListeners(){
		this.handButton.off('click',this.handBtnClickHandler.bind(this)).on('click',this.handBtnClickHandler.bind(this));
	}

	lowerTheHand() {
		this.testerWindow.kendoWindow.wrapper.find('button[aria-label="Hand"]').removeClass('rtp-active-toolsTabBtn');
		this.testerWindow.isIconHandRaised = false;

		// Change the button to reflect the new state
		this.testerWindow.handTooltip.refresh();
	}

	raiseTheHand() {
		this.testerWindow.kendoWindow.wrapper.find('button[aria-label="Hand"]').addClass('rtp-active-toolsTabBtn');
		this.testerWindow.isIconHandRaised = true;

		// Change the button to reflect the new state
		this.testerWindow.handTooltip.refresh();
	}

	handBtnClickHandler() {
		// Handle the raise/lower of the hand
		if (this.testerWindow.isIconHandRaised) {
			this.sendLowerHandMessage();
			this.lowerTheHand();
		} else {
			this.sendRaiseHandMessage();
			this.raiseTheHand();
		}
	}

	handleHandMessage(message) {
		if (message.data.eventType === Constants.EventTypes.LOWER_HAND) {
			if (this.config.lowerHandHandler) {
				this.config.lowerHandHandler(message);
			} else {
				this.lowerTheHand();
			}
		} else if (!message.data.isHistorical && message.data.eventType === Constants.EventTypes.RAISE_HAND) {
			this.raiseTheHand();
		}
	}

	handleChatMessage(message) {
		switch(message.data.eventType) {
			case Constants.EventTypes.INITIATE_CHAT:
				this.initiateChat();
				break;
			case Constants.EventTypes.RECEIVED_CHAT:
				let messages = this.testerWindow.chatDiv.find(".rtpChatMessage");
				if (messages && messages.length > 0) {
					for (let msgIdx = 0; msgIdx < messages.length; msgIdx++) {
						let messageDiv = messages[msgIdx];
						if (messageDiv && messageDiv.id == message.data.msgId) {
							let fontIcons = messageDiv.getElementsByClassName('k-font-icon');
							for (let i = 0; i < fontIcons.length; i++) {
								let fontRandomId=crypto.randomUUID();
								fontIcons[i].id = 'font-'+fontRandomId;
								let tooltipElement = $k('#font-'+fontRandomId);
								if (message.data.msgSeen) {
									this.testerWindow.changeChatStatusIcon(fontIcons[i], tooltipElement, CustomKendoWindow.CHAT_MSG_SEEN);
								} else {
									this.testerWindow.changeChatStatusIcon(fontIcons[i], tooltipElement, CustomKendoWindow.CHAT_MSG_RECEIVED);
								}
							}
						}
					}
				}
				break;
			case Constants.EventTypes.SEND_CHAT:
				this.testerWindow.postMessage(message);
				this.sendReceivedChatMessage(this.testerWindow, this.testerIdentifier, message.data.msgId);
				this.showChatBox();
				break;
			case Constants.EventTypes.END_CHAT:
				this.endChat();
				break;
		}
	}

	handleImageMessage(message) {
		if (Constants.EventTypes.CHANGE_IMAGE_FREQUENCY === message.data.eventType) {
			this.captureImage.setImageFrequency(message.data.text);
		}
	}

	showChatBox() {
		this.testerWindow.isChatEnabled = true;
		this.testerWindow.kendoChat.wrapper.find('.k-message-box').show();
	}

	hideChatBox() {
		this.testerWindow.isChatEnabled = false;
		this.testerWindow.kendoChat.wrapper.find('.k-message-box').hide();
	}

	handleCallMessage(message) {
		switch(message.data.eventType) {
			case Constants.EventTypes.INITIATE_CALL:
				if (!this.isInAnyCall()) {
					this.testerWindow.handleCallRecieved();
					this.meetingId = message.data.text;
					this.activeCallProctorId = message.data.clientId;
					this.postNotificationMsg(rtp_i18next.t("testerWidget:proctorStartedCall"));
					this.startCallChime();
					this.createAnswerCallTimeout();
				} else {
					//Two proctors tried calling the same tester.  Rather than trying to decide which proctor called first, let's just end the call.
					this.endAppropriateCall();
					const messageContents = {'activeCallProctorId' : this.activeCallProctorId};
					this.publishMessage(this.testerIdentifier,
						RTPaaS.createMessage(
							Payload.createPayload(
								this.clientId,
								Constants.Role.TESTER,
								this.testerIdentifier,
								Constants.MessageNames.CALL,
								Constants.EventTypes.TESTER_ALREADY_IN_CALL,
								messageContents
							)
						)
					);
				}
				break;
			case Constants.EventTypes.END_CALL:
				//If the tester sent the END_CALL message (by leaving the channel), there is no need to do anything.
				if (Constants.Role.TESTER === message.data.senderType) {
					break;
				}
				this.postNotificationMsg(rtp_i18next.t("testerWidget:proctorEndedCall"));
				this.cancelAnswerCallTimeout();
				this.endCallAndResetVideoWindow();
				break;
			case Constants.EventTypes.PROCTOR_FAILED_TO_CONNECT_CALL:
				this.proctorFailedToConnect();
				break;
			case Constants.EventTypes.VIDEO_MONITORING_CALL:
				this.config.monitoringType = Constants.MonitoringType.BY_VIDEO;
				if(!this.activeVideoMonitoringInitialization) {
					this.handleVideoMonitoringCall(message.data);
				}
				break;
			case Constants.EventTypes.UNLOCK_TESTER:
				this.handleUnlockTester(message.data);
				break;
			case Constants.EventTypes.FORCE_EXIT_CALL:
				this.forceExitCall();
				break;
		}
	}

	//Notify the tester that the proctor has forced them to exit the test.
	forceExitCall(){
		this.notifyStudentExit();
	}

	//End either the video call or the group call, depending on which one is running.
	endAppropriateCall() {
		if (this.testerWindow.isVideoCallRunning() || this.testerWindow.isBeingCalled()) {
			this.postNotificationMsg(rtp_i18next.t("testerWidget:proctorEndedCall"));
			this.cancelAnswerCallTimeout();
			this.endCallAndResetVideoWindow();
		}
		if (this.getIsGroupAudioCallRunning()) {
			this.handleEndBroadcastCall();
			this.activeGroupCallProctorId = null;
		}
	}

	endCallAndResetVideoWindow() {
		this.testerWindow.endCallHandler( this.testerIdentifier);
		this.activeCallProctorId = null;
		this.testerWindow.videoWindow.endCall( this.testerIdentifier);
		this.stopCallChime();
		this.cancelAnswerCallTimeout();
		this.toggleCallOff();
	}

	proctorFailedToConnect() {
		this.endCallAndResetVideoWindow();
		this.postNotificationMsg(rtp_i18next.t("testerWidget:proctorCallFailed"));
	}

	proctorLostConnection() {
		let postNotification = false;
		if (this.testerWindow && this.testerWindow.isVideoCallRunning()) {
			postNotification = true;
		}
		this.endCallAndResetVideoWindow();
		if (postNotification) {
			this.postNotificationMsg(rtp_i18next.t("testerWidget:proctorEndedCall"));
		}
	}

	createAnswerCallTimeout() {
		var that = this;
		if (!this.answerCallTimeoutId) {
			this.answerCallTimeoutId = setTimeout(function () {
				that.answerCallHasTimedOut();
			}, 30 * 1000);
		}
	}

	answerCallHasTimedOut() {
		this.endCallAndResetVideoWindow();
		this.postNotificationMsg(rtp_i18next.t("testerWidget:testerMissedCall"));
		this.publishMessage(
			this.testerIdentifier,
			RTPaaS.createMessage(
				Payload.createPayload(
					this.clientId,
					Constants.Role.TESTER,
					this.testerIdentifier,
					Constants.MessageNames.CALL,
					Constants.EventTypes.UNANSWERED_CALL
				)
			)
		);
	}

	cancelAnswerCallTimeout() {
		if (this.answerCallTimeoutId) {
			clearTimeout(this.answerCallTimeoutId);
			this.answerCallTimeoutId = null;
		}
	}

	postNotificationMsg(textMsg) {
		let kendoWindow = this.testerWindow.kendoWindow;
		let kendoChat = this.testerWindow.kendoChat;
		if (kendoWindow.isMinimized()) {
			kendoWindow.restore();
		}
		kendoChat.renderAttachments({
			attachments: [{
				contentType: "notification",
				content: {
					title: "Notification",
					text: textMsg,
					timestamp: new Date()
				}
			}],
			attachmentLayout: "list"
		}, kendoChat.getUser());
	}

	initiateChat() {
		let kendoWindow = this.testerWindow.kendoWindow;
		let kendoChat = this.testerWindow.kendoChat;
		if (kendoWindow.isMinimized()) {
			kendoWindow.restore();
		}
		if (this.testerWindow.isChatEnabled) {
			return;
		}
		this.postNotificationMsg(rtp_i18next.t("testerWidget:proctorStartedChat"));
		this.showChatBox();
	}

	endChat() {
		//if the chat is already ended, no need to do anything
		if (!this.testerWindow.isChatEnabled) {
			return;
		}
		this.postNotificationMsg(rtp_i18next.t("testerWidget:proctorEndedChat"));
		//don't allow the tester to type anymore
		this.hideChatBox();
	}

	//tester specific functions
	sendLowerHandMessage() {
		this.publishMessage(this.testerIdentifier, RTPaaS.createMessage(Payload.createPayload(this.clientId, Constants.Role.TESTER, this.testerIdentifier, Constants.MessageNames.HAND, Constants.EventTypes.LOWER_HAND)));
	}

	sendRaiseHandMessage() {
		this.publishMessage(this.testerIdentifier, RTPaaS.createMessage(Payload.createPayload(this.clientId, Constants.Role.TESTER, this.testerIdentifier, Constants.MessageNames.HAND, Constants.EventTypes.RAISE_HAND)));
	}

	initiateCall(testerIdentifier) {
		console.log("initiateCall() START "+testerIdentifier);
	}

	async startVideoCall(testerIdentifier) {
		if (!this.testerWindow.isVideoCallRunning()) {
			this.testerWindow.setVideoCallRunning(true);
			let callResults = await this.testerWindow.videoWindow.startCall();
			if (!callResults) {
				return false;
			}
			// Tell the proctor you are responding
			this.publishMessage(
				testerIdentifier,
				RTPaaS.createMessage(
					Payload.createPayload(
						this.clientId,
						Constants.Role.TESTER,
						testerIdentifier,
						Constants.MessageNames.CALL,
						Constants.EventTypes.ANSWER_CALL
					)
				)
			);

			// Verify the proctor is in the call
			let channel = this.getChannelWithHistory(testerIdentifier);
			let that = this;
			this.isProctorPresent(channel).then(
				(isProctorPresent) => {
					if (isProctorPresent) {
						// Add a announcement, user has entered the call
						that.postNotificationMsg(rtp_i18next.t("testerWidget:testerAnsweredCall"));
					} else {
						// No proctor. End the call
						that.endCallAndSwitchToChat(testerIdentifier);
					}
				},
				(err) => {
					console.error(`startVideoCall() Error retrieving presence data: ${err}`);
				}
			);
		}
		return true;
	}

	endCall(testerIdentifier) {
		if (this.testerWindow.isVideoCallRunning()) {
			this.publishMessage(
				testerIdentifier,
				RTPaaS.createMessage(
					Payload.createPayload(
						this.clientId,
						Constants.Role.TESTER,
						testerIdentifier,
						Constants.MessageNames.CALL,
						Constants.EventTypes.TESTER_LEFT_CALL
					)
				)
			);
			this.testerWindow.videoWindow.endCall();

			this.postNotificationMsg(rtp_i18next.t("testerWidget:testerLeftCall"));
			this.toggleCallOff();
			document.querySelector('#displayVideoStatusText_' + this.testerIdentifier).innerHTML = 'Test Monitor calling';
		}
	}

	endCallDueToError(testerIdentifier, extraDisplayText) {
		this.publishMessage(testerIdentifier, RTPaaS.createMessage(Payload.createPayload(this.clientId, Constants.Role.TESTER, testerIdentifier, Constants.MessageNames.CALL, Constants.EventTypes.TESTER_FAILED_TO_CONNECT_CALL)));
		this.endCallAndResetVideoWindow();
		if (extraDisplayText) {
			this.postNotificationMsg(rtp_i18next.t("testerWidget:testerCallFailed") + " " + extraDisplayText);
		} else {
			this.postNotificationMsg(rtp_i18next.t("testerWidget:testerCallFailed"));
		}
		document.querySelector('#displayVideoStatusText_' + this.testerIdentifier).innerHTML = 'Test Monitor calling';
	}

	isSwitchToVideoAllowed(testerIdentifier) {
		return true;
	}

	switchToVideo(testerIdentifier) {
		if (!this.testerWindow.isVideoCallRunning()) {
			this.enableVideoToolBarBtns(testerIdentifier);
			this.testerWindow.videoStatusMsg(testerIdentifier, 'Test Monitor calling');
		} else {
			this.testerWindow.videoStatusMsg(testerIdentifier, 'Connected');
		}
	}

	enableVideoToolBarBtns(testerIdentifier) {
		this.testerWindow.videoWindow.setAudioIconStatus(true, true);
	}

	disableAllOtherStartButtons(testIdentifierToKeep) {
		// Do nothing
	}

	determineAllStartButtonStatuses(testIdentifierToKeep) {
		// Do nothing
	}

	setVideoStatusText(testerIdentifier, message) {
		// Do nothing
	}

	chat(testerIdentifier, text, msgId) {
		const messageContents = { text : text, msgId: msgId };
		this.publishMessage(this.testerIdentifier, RTPaaS.createMessage(Payload.createPayload(this.clientId, Constants.Role.TESTER,
			testerIdentifier, Constants.MessageNames.CHAT, Constants.EventTypes.SEND_CHAT, messageContents)));
		let that = this;
		setTimeout(function () {
			that.checkIfMessageWasReceived(msgId);
		}, 2 * 1000);
	}

	checkIfMessageWasReceived(msgId) {
		let messages = this.testerWindow.chatDiv.find(".rtpChatMessage");
		if (messages && messages.length > 0) {
			for (let msgIdx = 0; msgIdx < messages.length; msgIdx++) {
				let messageDiv = messages[msgIdx];
				if (messageDiv && messageDiv.id == msgId) {
					let fontIcons = messageDiv.getElementsByClassName('k-font-icon');
					for (let i = 0; i < fontIcons.length; i++) {
						if (!fontIcons[i].classList.contains('k-i-check-outline') && !fontIcons[i].classList.contains('k-i-check-circle')) {
							let fontRandomId = crypto.randomUUID();
							fontIcons[i].id = 'font-' + fontRandomId;
							let tooltipElement = $k('#font-' + fontRandomId);
							this.testerWindow.changeChatStatusIcon(fontIcons[i], tooltipElement, CustomKendoWindow.CHAT_MSG_NOTSEEN);
						}
					}
				}
			}
		}
	}

	sendImageToProctor(imgData){
		if (!this.isAblySuspended){
			this.isProctorPresent(this.getChannelWithHistory(this.testerIdentifier)).then(
				(isProctorPresent) => {
					if (isProctorPresent) {
						this.publishMessageWithoutHistory(
							this.testerIdentifier,
							RTPaaS.createMessage(
								Payload.createPayload(
									this.clientId,
									Constants.Role.TESTER,
									this.testerIdentifier,
									Constants.MessageNames.IMAGE,
									Constants.EventTypes.TESTER_IMAGE,
									{ text: imgData }
								)
							)
						);
					}
				},
				(err) => {
					console.error(`sendImageToProctor() Error retrieving presence data: ${err}`);
				}
			);
		}
	}

	async initPresenceHandlers(channel) {
		let that = this;
		await channel.presence.subscribe('enter', function(member) {
			if (member.clientId === that.clientId) {
				//inform the proctor(s) that the tester isn't currently in a call
				that.publishMessage(that.testerIdentifier, RTPaaS.createMessage(Payload.createPayload(that.clientId, Constants.Role.PROCTOR, that.testerIdentifier, Constants.MessageNames.CALL, Constants.EventTypes.TESTER_NOT_IN_CALL)));

				//if the tester joins before a proctor is present, kick them out within a certain time period.
				that.isProctorPresent(channel).then(
					(isProctorPresent) => {
						if (!isProctorPresent) {
							that.startNoProctorAvailableTimeout(30);
						}
					}
				);
			}

			if (that.testerWindow.isIconHandRaised) {
				that.sendRaiseHandMessage();
			} else {
				that.sendLowerHandMessage();
			}
			if (member.data.isProctor) {
				that.clearNoProctorAvailableTimeout();
			}
		});

		await channel.presence.subscribe('leave', function (member) {
			if (member.clientId === that.clientId) {
				return;
			}

			//If initialization hasn't finished, don't do anything
			if (!that.isInitialized) {
				return;
			}

			//Proctor has left.  End the video call if there is one.
			if ((that.testerWindow.isVideoCallRunning() || that.testerWindow.isBeingCalled()) && that.activeCallProctorId === member.clientId) {
				//Send a message as the proctor informing the other proctors so that they can update their call buttons.  This needs to be sent as an ably message so that it will be re-played in history
				that.publishMessage(that.testerIdentifier, RTPaaS.createMessage(Payload.createPayload(member.clientId, Constants.Role.PROCTOR, that.testerIdentifier, Constants.MessageNames.CALL, Constants.EventTypes.END_CALL)));
				that.proctorLostConnection();
			}

			//if we are in a group call and the proctor that started the call is leaving, end the call and send a message to inform other proctors.
			//Note: this is not ideal, since this will send a message for every tester that is in the group call.
			if (that.getIsGroupAudioCallRunning() && that.activeGroupCallProctorId === member.clientId) {
				that.publishMessage(that.broadcastChannelIdentifier, RTPaaS.createMessage(Payload.createPayload(member.clientId, Constants.Role.PROCTOR, that.testerIdentifier, Constants.MessageNames.BROADCAST, Constants.EventTypes.BROADCAST_END_CALL)));
				that.handleEndBroadcastCall();
			}

			//Proctor has left.  If there are no remaining proctors, end the chat.
			that.isProctorPresent(channel).then(
				(isProctorPresent) => {
					if (!isProctorPresent) {
						that.endChat();
						that.publishMessage(that.testerIdentifier, RTPaaS.createMessage(Payload.createPayload(that.clientId, Constants.Role.TESTER, that.testerIdentifier, Constants.MessageNames.CHAT, Constants.EventTypes.END_CHAT)));
						that.startNoProctorAvailableTimeout(30);
					}
				}
			);
		});
	}

	endCallAndSwitchToChat(testerIdentifier) {
		if (this.testerWindow && this.testerWindow.isVideoCallRunning()) {
			this.endCallAndResetVideoWindow();
			this.postNotificationMsg(rtp_i18next.t("testerWidget:proctorEndedCall"));
		}
	}

	handleHistory() {
		let that = this;
		this.retrieveHistory(this.testerIdentifier, Constants.VideoCapture.MAX_LOCK_STATUS_AGE_MN).then(
			(history) => {
				that.replayHistory(that, history).then(() =>{});
			},
			(err) => {
				console.error(`handleHistory() ${err}`);
			}
		).finally(() => {
			this.testerWindow.chatDiv.find('.notification').parent().remove();
			this.testerWindow.windowDiv.removeClass('rtp-loading');
			this.testerWindow.windowDiv.find('.rtp-loading-icon').remove();
		});
	}

	isProctor() {
		return false;
	}

	//Start the system check process.
	// Return a promise that resolves on success and rejects on failure.
	initSystemCheck(initData){
		let systemCheckSuccess, systemCheckFail;

		this.systemCheckObj = new SystemCheck();
		const { systemCheckViewElement } = initData;
		if (!systemCheckViewElement) {
			throw new Error("RTP: systemCheckViewElement is required for system check initialization.");
		}
		this.systemCheckObj.initSystemCheckWelcome(systemCheckViewElement, this).then(
			(successData) => {
				this.systemCheckSuccessHandler(successData,systemCheckSuccess);
			},
			(errData)=>{
				this.systemCheckFailHandler(errData,systemCheckFail);
			}
		);

		// Return a promise for Test Delivery system to consume
		return new Promise(function(resolve, reject) {
			systemCheckSuccess = resolve;
			systemCheckFail = reject;
		});
	}

	systemCheckSuccessHandler(successData,systemCheckSuccess){
		this.setMicId(successData.micId);
		this.setSpeakerId(successData.speakerId);
		this.setCameraId(successData.cameraId);

		//Notifying success to Test Delivery
		systemCheckSuccess(successData);

		//Notifying success to Test Management
		this.sendSystemCheckSuccessStatus(successData);

	}

	systemCheckFailHandler(errData,systemCheckFail){
		//Notifying failure to Test Delivery
		systemCheckFail(errData);

		//Notifying failure to Test Management
		this.sendSystemCheckFailureStatus(errData);
	}

	sendSystemCheckSuccessStatus(successData){
		const messageContents = {'isSuccess': true}
		this.publishMessage(this.testerIdentifier, RTPaaS.createMessage(Payload.createPayload(this.clientId, Constants.Role.TESTER, this.testerIdentifier, Constants.MessageNames.SYSTEM_CHECK, Constants.EventTypes.SYSTEM_CHECK_SUCCESS,messageContents)));
	}

	sendSystemCheckFailureStatus(failureObj){
		const messageContents = {'isSuccess': false, 'statusCode' : failureObj.failureCode, 'statusMsg': failureObj.failureMsg};
		this.publishMessage(this.testerIdentifier, RTPaaS.createMessage(Payload.createPayload(this.clientId, Constants.Role.TESTER, this.testerIdentifier, Constants.MessageNames.SYSTEM_CHECK, Constants.EventTypes.SYSTEM_CHECK_FAIL,messageContents)));
	}

	toggleCallOff() {
		this.testerWindow.setVideoCallRunning(false);
		this.testerWindow.setIsBeingCalled(false);
	}

	getMicId(){
		return this._micId;
	}
	getSpeakerId(){
		return this._speakerId;
	}
	getCameraId(){
		return this._cameraId;
	}

	setMicId(micId){
		this._micId = micId;
	}
	setSpeakerId(speakerId){
		//update the speaker that the call chime will be played through.
		this.receivingCallAudio.setSinkId?.(speakerId);
		this._speakerId = speakerId;
	}
	setCameraId(cameraId){
		this._cameraId = cameraId;
		this.captureImage.changeCaptureImageDevice(cameraId);
	}

	onConnection(disconnectDate) {
		let that = this;
		if (disconnectDate) {
			this.retrieveHistoryFromDate(this.testerIdentifier, disconnectDate).then(
				(history) => {
					that.replayHistory(that, history).then(() =>{});
				},
				(err) => {
					console.error("RTPaasTester.onConnection() ERROR "+err);
				}
			);
		}
	}

	sendReceivedChatMessageOnly(proctorWindow, testerIdentifier, msgId, msgSeen) {
		const messageContents = {
			msgId : msgId,
			msgSeen : msgSeen
		};
		this.publishMessage(
			testerIdentifier,
			RTPaaS.createMessage(
				Payload.createPayload(
					this.clientId,
					Constants.Role.TESTER,
					testerIdentifier,
					Constants.MessageNames.CHAT,
					Constants.EventTypes.RECEIVED_CHAT,
					messageContents
				)
			)
		);
	}

	sendReceivedChatMessage(testerWindow, testerIdentifier, msgId) {
		let msgSeen = true;
		if ((testerWindow && testerWindow.unseenChatCount > 0) || document.visibilityState == 'hidden') {
			msgSeen = false;
		}
		this.sendReceivedChatMessageOnly(testerWindow, testerIdentifier, msgId, msgSeen);
		if (!msgSeen) {
			if (!this.unseenMessagesAry) {
				this.unseenMessagesAry = [];
			}
			let messageToSee = { testerIdentifier : testerIdentifier, msgId : msgId };
			this.unseenMessagesAry.push(messageToSee);
		}
	}

	sendUnseenReceivedChatMessage(testerIdentifier) {
		if (this.unseenMessagesAry && this.unseenMessagesAry.length > 0) {
			if (this.testerWindow) {
				for (let unseenMessage of this.unseenMessagesAry) {
					this.sendReceivedChatMessageOnly(this.testerWindow, unseenMessage.testerIdentifier, unseenMessage.msgId, true);
				}
				this.unseenMessagesAry = [];
			}
		}
	}

	toString() {
		return `${this.constructor.name}=[clientId=${this.clientId}, testerIdentifier=${this.testerIdentifier}, micId=${this.getMicId()}, cameraId=${this.getCameraId()}, speakerId=${this.getSpeakerId()}]`;
	}

	//broadcast chat and group audio call
	handleBroadcastMessage(message){
		switch(message.data.eventType){
			case Constants.EventTypes.BROADCAST_CHAT:
				this.testerWindow.postBroadcastMsg(message);
				let kendoWindow = this.testerWindow.kendoWindow;
				if (kendoWindow.isMinimized()) {
					kendoWindow.restore();
				}
				break;

			case Constants.EventTypes.BROADCAST_CALL:
				if (this.isInAnyCall()) {
					this.endAppropriateCall();
					const messageContents = {
						'activeGroupCallProctorId' : this.activeGroupCallProctorId,
						'isBroadcastCall' : true
					};
					this.publishMessage(
						this.testerIdentifier,
						RTPaaS.createMessage(
							Payload.createPayload(
								this.clientId,
								Constants.Role.TESTER,
								this.testerIdentifier,
								Constants.MessageNames.CALL,
								Constants.EventTypes.TESTER_ALREADY_IN_CALL,
								messageContents
							)
						)
					);
					break;
				}
				this.handleBroadcastCall(message.data.meetingId);
				this.activeGroupCallProctorId = message.data.clientId;
				this.broadcastChannelIdentifier = message.data.broadcastChannelIdentifier;
				break;
			case Constants.EventTypes.BROADCAST_END_CALL:
				if (!this.getIsGroupAudioCallRunning()) {
					break;
				}
				this.handleEndBroadcastCall();
				this.activeGroupCallProctorId = null;
				break;
			case Constants.EventTypes.BROADCAST_TOGGLE_MUTE:
				this.setGroupCallMuteStatus(message.data.muted);
				break;
		}
	}

	handleBroadcastCall(meetingId){
		this.startCallChime();
		this.setIsGroupAudioCallRunning(true);
		this.testerWindow.handleBroadcastCall();
		this.meetingId = meetingId;
		this.initializeAutoConnectTimer();
		this.postNotificationMsg(rtp_i18next.t("testerWidget:proctorStartedGroupCall"));
	}

	handleUnlockTester() {
		this.isLocked = false;
		$(document).trigger('unlockTester');
	}

	handleVideoMonitoringCall(videoMonitoringMeetingData) {
		this.activeVideoMonitoringInitialization = true;
		this.initVideoForVideoMonitoringCall(videoMonitoringMeetingData).then((videoMonitoringMeetingId)=> {
			this.videoMonitoringMeetingId = videoMonitoringMeetingId;
			console.log("Video monitoring call started successfully with meetingId: " + videoMonitoringMeetingId);
			// To update the tester's video monitoring status in history
			const messageContents = {
				videoMonitoringStatus: Constants.TesterVideoConnectionToSession.CONNECTING
			};
			this.publishMessage(
				this.testerIdentifier,
				RTPaaS.createMessage(
					Payload.createPayload(
						this.clientId,
						Constants.Role.TESTER,
						this.testerIdentifier,
						Constants.MessageNames.CALL,
						Constants.EventTypes.ANSWER_VIDEO_MONITORING_CALL,
						messageContents
					)
				)
			);
			this.activeVideoMonitoringInitialization = false;
		}).catch((err) => {
			console.error("Error starting video monitoring call: " + err);
			this.publishMessage(this.testerIdentifier,
				RTPaaS.createMessage(
					Payload.createPayload(
						this.clientId,
						Constants.Role.TESTER,
						this.testerIdentifier,
						Constants.MessageNames.CALL,
						Constants.EventTypes.TESTER_FAILED_TO_CONNECT_VIDEO_MONITORING_CALL)));
		});
	}

	setGroupCallMuteStatus(muted) {
		if (!this.groupAudioVideoClient) {
			return;
		}
		if (muted) {
			this.groupAudioVideoClient.muteLocalAudio();
		} else {
			this.groupAudioVideoClient.unMuteLocalAudio();
		}
		this.testerWindow.toggleLocalGroupMuteIcon(muted);
	}

	initializeAutoConnectTimer(){
		this.autoConnectTimer = setInterval(this.autoConnectBroadcastCall.bind(this), 20000)
	}

	joinGroupAudioCallClicked(){
		clearInterval(this.autoConnectTimer);
		this.stopCallChime();
		this.initAudioVideoClientForGroupCall(this.meetingId);
	}

	restartVideoMonitoringByTester(){
		if(!this.videoMonitoringMeetingId) {
			return;
		}
		//Send message to proctor that tester is restarting video monitoring
		const messageContents = {
			videoMonitoringStatus: Constants.TesterVideoConnectionToSession.RESTARTING
		}
		this.publishMessage(this.testerIdentifier,
			RTPaaS.createMessage(Payload.createPayload(
				this.clientId,
				Constants.Role.TESTER,
				this.testerIdentifier,
				Constants.MessageNames.CALL,
				Constants.EventTypes.TESTER_RESTARTING_VIDEO_MONITORING,
				messageContents
			)));
	}

	initVideoForVideoMonitoringCall(meetingData) {
		return new Promise(async (resolve, reject) => {
			try {
				const params = {
					context: this,
					clientId: this.testerIdentifier,
					meetingId: meetingData?.meetingId,
					isAttendeeProctor: false,
					isVideoMonitoringCall: true,
					videoQuality: meetingData?.videoQuality,
					videoIsSuspendedCallback: this.handleVideoSuspended.bind(this)
				};
				if (this.monitoringAudioVideoClient) {
					await this.monitoringAudioVideoClient.removeAttendeeByTester();
					this.monitoringAudioVideoClient.cleanup();
					this.monitoringAudioVideoClient = null;
				}
				this.monitoringAudioVideoClient = new AudioVideoClient(params);
				const meetingId = await this.monitoringAudioVideoClient.startCall();
				if (meetingId) {
					console.log("Successfully started video monitoring call with meetingId: " + meetingId);
					resolve(meetingId);
				} else {
					reject("Error initializing video for video monitoring call. Meeting ID is null.");
				}
			} catch (err) {
				console.error("Error initializing video for video monitoring call: " + err);
				reject(err);
			}
		});
	}

	// Callback when video is suspended due to network issues or other reasons.
	handleVideoSuspended() {
		if( !this.videoMonitoringMeetingId ) {
			return;
		}
		// To update the tester's video monitoring status in history
		const messageContents = {
			videoMonitoringStatus: Constants.TesterVideoConnectionToSession.SUSPENDED
		};
		this.publishMessage(this.testerIdentifier,
			RTPaaS.createMessage(Payload.createPayload(
				this.clientId,
				Constants.Role.TESTER,
				this.testerIdentifier,
				Constants.MessageNames.CALL,
				Constants.EventTypes.TESTER_SUSPEND_VIDEO_MONITORING,
				messageContents
			)));
	}

	async initAudioVideoClientForGroupCall(meetingId){
		let params = {
			clientId : this.testerIdentifier,
			meetingId : meetingId,
			isGroupCall: true,
			remoteMuteAudioCallback : this.toggleProctorMuteButton.bind(this)
		};

		this.groupAudioVideoClient = new AudioVideoClient(params);
		await this.groupAudioVideoClient.startCall();
	}

	toggleProctorMuteButton(isMuted) {
		this.testerWindow.toggleProctorGroupMuteIcon(isMuted);
	}

	autoConnectBroadcastCall(){
		clearInterval(this.autoConnectTimer);
		this.stopCallChime();
		this.playAutoConnectCallMsg();
		this.testerWindow.handleJoinGroupCallClick();
	}

	playAutoConnectCallMsg(){
		let audioCallAutoConnectMsg = new Audio(AudioCallAutoConnectMsg_Mp3);
		if (audioCallAutoConnectMsg) {
			audioCallAutoConnectMsg.play();
		}
	}

	handleEndBroadcastCall(){
		this.stopCallChime();
		this.setIsGroupAudioCallRunning(false);
		clearInterval(this.autoConnectTimer);
		this.testerWindow.handleEndBroadcastCall();
		this.postNotificationMsg(rtp_i18next.t("testerWidget:proctorEndedGroupCall"));
	}

	isInAnyCall() {
		return this.testerWindow.isVideoCallRunning() || this.testerWindow.isBeingCalled() || this.getIsGroupAudioCallRunning();
	}

	async cleanupController() {
		// Stop image streaming
		this.captureImage?.destroy();
		if (this.imgCaptureInterval) {
			clearInterval(this.imgCaptureInterval);
			this.imgCaptureInterval = null;
		}
		this.videoMonitoringMeetingId = null;
		// Remove attendee and notify
		if (this.monitoringAudioVideoClient) {
			await this.monitoringAudioVideoClient.removeAttendeeByTester();
			// To update the tester's video monitoring status in history
			const messageContents = {
				videoMonitoringStatus: Constants.TesterVideoConnectionToSession.DISCONNECTED
			};
			this.publishMessage(
				this.testerIdentifier,
				RTPaaS.createMessage(
					Payload.createPayload(
						this.clientId,
						Constants.Role.TESTER,
						this.testerIdentifier,
						Constants.MessageNames.CALL,
						Constants.EventTypes.LEFT_VIDEO_MONITORING_CALL,
						messageContents
					)
				)
			);
			this.monitoringAudioVideoClient = null;
		} else {
			this.publishMessageWithoutHistory(
				this.testerIdentifier,
				RTPaaS.createMessage(Payload.createPayload(
					this.clientId,
					Constants.Role.TESTER,
					this.testerIdentifier,
					Constants.MessageNames.IMAGE,
					Constants.EventTypes.TESTER_LEFT
				))
			);
		}
		console.log("Monitoring audio video client ended successfully.");
		// Clean up Ably, UI, and system check
		super.cleanupController(this.testerIdentifier);
		this.testerWindow?.cleanupController();
		this.testerWindow?.isVideoCallRunning() && this.testerWindow.videoWindow.endCall(this.testerIdentifier);
	}
}
